/*
 * Listas de inventario de productos.
 *
 * Contiene los componentes que cargan la información de las listas de Todos los products,
 * productos de bajo inventario y productos fuera de stock.
 *
 * Created on Wed Jun 14 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>
    <!-- #region begin:title -->
    <b-form-row v-if="!$ability.can('read', 'Low_inventory') && !$ability.can('read', 'Out_stock')">
      <b-col
        cols="12"
        class="mb-1"
      >
        <h3>Productos</h3>
      </b-col>
      <b-col
        cols="12"
        class="mb1"
      >
        <b-card-text>
          <!-- #region::Product list -->
          <ProductsList
            resource="products"
            :highlight-text="true"
            :show-edit-button="true"
            :show-delete-button="true"
            :show-options-buttons="true"
            :show-add-to-circuit-button="true"
            :selectable-pieces-list="$ability.can('edit', 'Circuit')"
          />
          <!-- #endregion::Product list -->
        </b-card-text>
      </b-col>
    </b-form-row>
    <!-- #endregion end:title -->

    <!-- #region::Tabs sections -->
    <b-tabs
      v-else
      v-model="filteredInventorySelectedIndex"
      @input="selectedTab"
      @activate-tab="activateTab"
    >

      <!-- #region:: All products list tab -->
      <b-tab
        v-if="$ability.can('read', 'Products')"
        class="mb-1"
        lazy
      >

        <template #title>
          <span>Todos los productos</span><b-badge
            v-if="filteredSearchTerm && filteredProductsCount !== null && !loadingFilteredLists"
            class="ml-50"
            :variant="filteredProductsCount > 0 ? 'warning' : 'secondary'"
          >
            {{ filteredProductsCount }}
          </b-badge>
        </template>

        <b-card-text>
          <ProductsList
            resource="products"
            :highlight-text="true"
            :show-edit-button="true"
            :show-delete-button="true"
            :show-options-buttons="true"
            :show-add-to-circuit-button="true"
            :selectable-pieces-list="$ability.can('edit', 'Circuit')"
          >
            <div
              v-if="filteredSearchTerm && filteredProductsCount"
              slot="filteredProductsCount"
              class="mt-1"
            >
              {{ filteredProductsCount }} resultado(s) para "{{ filteredSearchTerm }}"
            </div>
          </ProductsList>
        </b-card-text>
      </b-tab>
      <!-- #endregion:: All products list tab -->

      <!-- #region::Low inventory products list tab -->
      <b-tab
        v-if="$ability.can('read', 'Low_inventory')"
        lazy
      >

        <template #title>
          <span>Bajo inventario</span><b-badge
            v-if="filteredSearchTerm && filteredLowInventoryProductsCount !== null && !loadingFilteredLists"
            class="ml-50"
            :variant="filteredLowInventoryProductsCount > 0 ? 'warning' : 'secondary'"
          >
            {{ filteredLowInventoryProductsCount }}
          </b-badge>
        </template>

        <b-card-text>
          <ProductsList
            resource="products?inventory=low"
            :highlight-text="true"
            :show-edit-button="true"
            :show-delete-button="true"
            :show-add-to-circuit-button="true"
            :selectable-pieces-list="$ability.can('edit', 'Circuit')"
          >
            <div
              v-if="filteredSearchTerm && filteredLowInventoryProductsCount"
              slot="filteredProductsCount"
              class="mt-1"
            >
              {{ filteredLowInventoryProductsCount }} resultado(s) para "{{ filteredSearchTerm }}"
            </div>
          </ProductsList>
        </b-card-text>
      </b-tab>
      <!-- #endregion::Low inventory products list tab -->

      <!-- #region::Out stock products list tab -->
      <b-tab
        v-if="$ability.can('read', 'Out_stock')"
        lazy
      >

        <template #title>
          <span>Fuera de stock</span><b-badge
            v-if="filteredSearchTerm && filteredOutInventoryProductsCount !== null && !loadingFilteredLists"
            class="ml-50"
            :variant="filteredOutInventoryProductsCount > 0 ? 'warning' : 'secondary'"
          >
            {{ filteredOutInventoryProductsCount }}
          </b-badge>
        </template>

        <b-card-text>
          <ProductsList
            resource="products?inventory=out"
            :highlight-text="true"
            :show-edit-button="true"
            :show-pieces-list="false"
            :show-delete-button="true"
          >

            <div
              v-if="filteredSearchTerm && filteredOutInventoryProductsCount"
              slot="filteredProductsCount"
              class="mt-1"
            >
              {{ filteredOutInventoryProductsCount }} resultado(s) para "{{ filteredSearchTerm }}"
            </div>

          </ProductsList>
        </b-card-text>
      </b-tab>
      <!-- #endregion::Out stock products list tab -->

    </b-tabs>
    <!-- #endregion::Tabs sections -->

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  BTabs, BTab, BCardText, BFormRow, BCol, BBadge,
} from 'bootstrap-vue'

import ProductsList from '@/modules/production/products/components/ProductsList.vue'

export default {
  components: {
    BTab,
    BCol,
    BTabs,
    BBadge,
    BFormRow,
    BCardText,
    ProductsList,
  },
  data() {
    const ALL_PRODUCTS = 0
    const LOW_INVENTORY = 1
    const OUT_OF_STOCK = 2

    return {
      selectedTabIndex: 0,

      ALL_PRODUCTS,
      LOW_INVENTORY,
      OUT_OF_STOCK,
    }
  },
  computed: {
    ...mapGetters({
      getFilteredPerPage: 'filters/getFilteredPerPage',
      getFilteredCurrentPage: 'filters/getFilteredCurrentPage',
      getLoadingFilteredLists: 'products/getLoadingFilteredLists',
      getFilteredProductsCount: 'products/getFilteredProductsCount',
      getFilteredLowInventoryProductsCount: 'products/getFilteredLowInventoryProductsCount',
      getFilteredOutInventoryProductsCount: 'products/getFilteredOutInventoryProductsCount',

      /**
       * Filtros globales
       */
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredInventorySelectedTab: 'filters/getFilteredInventorySelectedTab',
    }),
    /**
     * Indica la cantidad de elementos por página (paginado global)
     */
    filteredPerPage: {
      get() { return this.getFilteredPerPage },
      set(value) { this.setFilteredPerPage(value) },
    },
    /**
     * Indica la página actual (paginado global)
     */
    filteredCurrentPage: {
      get() { return this.getFilteredCurrentPage },
      set(value) { this.setFilteredCurrentPage(value) },
    },
    /**
     * Cantidad de productos filtrados por el término de búsqueda
     */
    filteredProductsCount: {
      get() { return this.getFilteredProductsCount },
    },
    /**
     * Cantidad de productos de bajo inventario filtrados por el término de búsqueda
     */
    filteredLowInventoryProductsCount: {
      get() { return this.getFilteredLowInventoryProductsCount },
    },
    /**
     * Cantidad de productos fuera de stock filtrados por el término de búsqueda
     */
    filteredOutInventoryProductsCount: {
      get() { return this.getFilteredOutInventoryProductsCount },
    },
    /**
     * Indica si se están cargando las listas filtradas
     */
    loadingFilteredLists: {
      get() { return this.getLoadingFilteredLists },
    },
    /**
     * Término de búsqueda (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
      set(value) { this.setFilteredSearchTerm(value) },
    },
    /**
     * Pestaña seleccionada de la lista de inventario de productos (filtro en store)
     */
    filteredInventorySelectedTab: {
      get() { return this.getFilteredInventorySelectedTab },
      set(value) { this.setFilteredInventorySelectedTab(value) },
    },
    filteredInventorySelectedIndex: {
      get() {
        switch (this.filteredInventorySelectedTab) {
          case 'all':
            return this.ALL_PRODUCTS
          case 'low':
            return this.LOW_INVENTORY
          case 'out':
            return this.OUT_OF_STOCK
          default:
            return this.ALL_PRODUCTS
        }
      },
      set() {},
    },
  },
  methods: {
    ...mapActions({
      setSearchTerm: 'products/setSearchTerm',

      /**
       * Filtros globales
       */
      setFilteredPerPage: 'filters/setFilteredPerPage',
      setFilteredSearchTerm: 'filters/setFilteredSearchTerm',
      setFilteredCurrentPage: 'filters/setFilteredCurrentPage',
      setFilteredInventorySelectedTab: 'filters/setFilteredInventorySelectedTab',
    }),

    /**
    * Evento de selección de pestaña
    *
    * @summary Establece el índice de la pestaña seleccionada
    * @param {Number} tabIndex - Índice de la pestaña seleccionada
    */
    selectedTab(tabIndex) {
      this.selectedTabIndex = tabIndex

      // En el cambio de pestaña se reinicia el paginado global
      this.filteredPerPage = 10
      this.filteredCurrentPage = 1

      switch (tabIndex) {
        case this.ALL_PRODUCTS:
          this.filteredInventorySelectedTab = 'all'
          break
        case this.LOW_INVENTORY:
          this.filteredInventorySelectedTab = 'low'
          break
        case this.OUT_OF_STOCK:
          this.filteredInventorySelectedTab = 'out'
          break
        default:
          break
      }
    },

    /**
     * Evento de activación de pestaña
     *
     * @summary Establece el término de búsqueda en vacío si se selecciona la
     * pestaña de todos los productos
     * @param {Number} newTabIndex - Índice de la nueva pestaña seleccionada
     * @param {Number} prevTabIndex - Índice de la pestaña previamente seleccionada
     */
    activateTab(newTabIndex, prevTabIndex) {
      if (newTabIndex === 0) {
        if (this.filteredProductsCount === null) {
          this.setSearchTerm('')
        }
      }

      if (newTabIndex === 1 && prevTabIndex === 2) {
        if (this.filteredLowInventoryProductsCount === null) {
          this.setSearchTerm('')
        }
      }

      if (newTabIndex === 2 && prevTabIndex === 1) {
        if (this.filteredOutInventoryProductsCount === null) {
          this.setSearchTerm('')
        }
      }
    },
  },
}
</script>
